import {
  callSummaryPreferenceQueryKey,
  getCallSummaryPreferenceQueryOptions,
  updateCallSummaryPreference,
} from "@/api/call-summary-preference";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Skeleton } from "./ui/skeleton";
import { Switch } from "./ui/switch";

export function CallSummaryPreference() {
  const queryClient = useQueryClient();
  const preference = useQuery(getCallSummaryPreferenceQueryOptions());
  const updatePreference = useMutation({
    mutationFn: updateCallSummaryPreference,
    onSuccess: (nextPreference) => {
      queryClient.setQueryData(
        callSummaryPreferenceQueryKey,
        nextPreference,
      );
      toast.success(
        nextPreference.enabled
          ? "Call summaries turned on"
          : "Call summaries paused",
      );
    },
    onError: () => {
      toast.error("We couldn't update your call summary preference");
    },
  });

  const enabled = preference.data?.enabled ?? false;
  const unavailable = preference.isError;

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <label
          htmlFor="call-summary-preference"
          className="text-sm font-medium text-neutral-700"
        >
          Post-call summaries
        </label>
        <p className="text-xs leading-relaxed text-neutral-500">
          {unavailable ? (
            <>
              We couldn&apos;t load this preference.{" "}
              <button
                type="button"
                className="cursor-pointer font-medium text-neutral-700 underline underline-offset-2 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                onClick={() => preference.refetch()}
              >
                Try Again
              </button>
            </>
          ) : (
            "Generate a summary after each call and send it to you in Slack."
          )}
        </p>
      </div>

      {preference.isPending ? (
        <Skeleton className="h-6 w-10 shrink-0 rounded-full" />
      ) : (
        <Switch
          id="call-summary-preference"
          aria-label="Post-call summaries"
          checked={enabled}
          disabled={unavailable || updatePreference.isPending}
          onCheckedChange={(checked) => updatePreference.mutate(checked)}
        />
      )}
    </div>
  );
}
